/**
 * Add/Edit child form — used by both "add child" and "edit child" flows.
 *
 * Birthday precision is a central UX: a parent might know only the
 * year, year+month, or the full year+month+day. The form exposes all
 * three fields with the rule "day requires month". Clearing the
 * month also clears the day in the UI, matching the backend's
 * cascading-clear semantics in `_apply_birth_patch`.
 *
 * Photo handling is isolated to `PhotoPicker`. The form stores a
 * pending Blob (if the user picked or captured one) and writes it
 * to IndexedDB via `lib/photos.ts` ONLY AFTER the server mutation
 * succeeds — so a failed create doesn't leave an orphaned photo.
 *
 * Validation is client-side only for friendliness — the backend
 * re-validates every field and will 422 if we miss something.
 */

import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "@/lib/api";
import { deleteChildPhoto, setChildPhoto } from "@/lib/photos";
import { usePhotoURL } from "@/lib/usePhotoURL";
import { PhotoPicker } from "@/features/children/PhotoPicker";
import {
  useCreateChild,
  useUpdateChild,
  type Child,
  type CreateChildInput,
  type UpdateChildInput,
} from "@/features/children/useChildren";

interface ChildFormProps {
  /** If provided, the form renders in edit mode. Otherwise, create mode. */
  initial?: Child;
  /** Called after a successful save. */
  onSaved: (child: Child) => void;
  /** Called when the user clicks Cancel. */
  onCancel: () => void;
}

type PhotoPending =
  | { kind: "unchanged" }
  | { kind: "replace"; blob: Blob }
  | { kind: "remove" };

export function ChildForm({ initial, onSaved, onCancel }: ChildFormProps) {
  const isEdit = initial !== undefined;

  const [name, setName] = useState(initial?.name ?? "");
  const [year, setYear] = useState<string>(
    initial?.birth_year !== undefined ? String(initial.birth_year) : "",
  );
  const [month, setMonth] = useState<string>(
    initial?.birth_month !== null && initial?.birth_month !== undefined
      ? String(initial.birth_month)
      : "",
  );
  const [day, setDay] = useState<string>(
    initial?.birth_day !== null && initial?.birth_day !== undefined
      ? String(initial.birth_day)
      : "",
  );
  const [notes, setNotes] = useState<string>(initial?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const [photoPending, setPhotoPending] = useState<PhotoPending>({
    kind: "unchanged",
  });
  // For display-only during edit: show the stored photo if we have one.
  const { url: storedUrl, refresh: refreshStoredUrl } = usePhotoURL(
    initial?.id ?? null,
  );
  // If the user picks a blob in this session, show it as the live preview.
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (photoPending.kind !== "replace") {
      setPendingPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(photoPending.blob);
    setPendingPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [photoPending]);

  const displayPhotoUrl: string | null =
    photoPending.kind === "remove"
      ? null
      : photoPending.kind === "replace"
        ? pendingPreviewUrl
        : storedUrl;

  const createChild = useCreateChild();
  const updateChild = useUpdateChild();
  const isSaving = createChild.isPending || updateChild.isPending;

  // Cascading clear: if the month is cleared, also clear the day.
  const handleMonthChange = (value: string) => {
    setMonth(value);
    if (value === "") {
      setDay("");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setError("Name can't be empty.");
      return;
    }

    const yearNum = Number.parseInt(year, 10);
    if (!Number.isInteger(yearNum) || yearNum < 1900 || yearNum > 2100) {
      setError("Year must be between 1900 and 2100.");
      return;
    }

    const monthNum = month === "" ? null : Number.parseInt(month, 10);
    if (monthNum !== null && (monthNum < 1 || monthNum > 12)) {
      setError("Month must be between 1 and 12.");
      return;
    }

    const dayNum = day === "" ? null : Number.parseInt(day, 10);
    if (dayNum !== null) {
      if (monthNum === null) {
        setError("Pick a month before setting a day.");
        return;
      }
      // Try to construct the real date — catches Feb 29, Apr 31, etc.
      const d = new Date(yearNum, monthNum - 1, dayNum);
      if (
        d.getFullYear() !== yearNum ||
        d.getMonth() !== monthNum - 1 ||
        d.getDate() !== dayNum
      ) {
        setError("That date doesn't exist. Check the month and day.");
        return;
      }
    }

    const notesTrimmed = notes.trim();
    const notesValue: string | null = notesTrimmed.length === 0 ? null : notesTrimmed;

    try {
      let savedChild: Child;
      if (isEdit && initial !== undefined) {
        const patch: UpdateChildInput = {
          name: trimmedName,
          birth_year: yearNum,
          birth_month: monthNum,
          birth_day: dayNum,
          notes: notesValue,
        };
        savedChild = await updateChild.mutateAsync({
          id: initial.id,
          patch,
        });
      } else {
        const body: CreateChildInput = {
          name: trimmedName,
          birth_year: yearNum,
          birth_month: monthNum,
          birth_day: dayNum,
          notes: notesValue,
        };
        savedChild = await createChild.mutateAsync(body);
      }

      // Server round-trip succeeded — NOW touch IndexedDB.
      if (photoPending.kind === "replace") {
        await setChildPhoto(savedChild.id, photoPending.blob);
      } else if (photoPending.kind === "remove") {
        await deleteChildPhoto(savedChild.id);
      }
      refreshStoredUrl();

      onSaved(savedChild);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail);
      } else {
        setError("Couldn't save. Try again?");
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 max-w-[640px]">
      <div className="flex flex-col gap-2">
        <label
          htmlFor="child-name"
          className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold"
        >
          Name
        </label>
        <input
          id="child-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isSaving}
          maxLength={60}
          placeholder="First name or nickname"
          autoFocus
          className="font-sans text-[16px] text-ink bg-surface border border-edge rounded-md px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60"
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
          Birthday
        </div>
        <p className="font-sans text-[13px] text-ink-muted max-w-[480px]">
          Year is required. Month and day are optional — fill in only what
          you're sure about. Some Perspectives need the full date to be exact.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="number"
            inputMode="numeric"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            disabled={isSaving}
            placeholder="Year"
            min={1900}
            max={2100}
            className="w-24 md:w-28 font-mono tabular-nums text-[16px] text-ink bg-surface border border-edge rounded-md px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60"
            aria-label="Birth year"
          />
          <select
            value={month}
            onChange={(e) => handleMonthChange(e.target.value)}
            disabled={isSaving}
            className="font-sans text-[14px] text-ink bg-surface border border-edge rounded-md px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60"
            aria-label="Birth month"
          >
            <option value="">Month (optional)</option>
            {MONTH_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <input
            type="number"
            inputMode="numeric"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            disabled={isSaving || month === ""}
            placeholder="Day"
            min={1}
            max={31}
            className="w-24 font-mono tabular-nums text-[16px] text-ink bg-surface border border-edge rounded-md px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60"
            aria-label="Birth day"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="child-notes"
          className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold"
        >
          Notes <span className="normal-case text-ink-muted italic">— optional</span>
        </label>
        <textarea
          id="child-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isSaving}
          maxLength={2000}
          rows={3}
          placeholder="Interests, favorite activities, anything that helps you remember them as they are right now."
          className="font-sans text-[14px] text-ink bg-surface border border-edge rounded-md px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60 resize-y"
        />
      </div>

      <PhotoPicker
        currentPhotoUrl={displayPhotoUrl}
        onPhotoSelected={(blob) => setPhotoPending({ kind: "replace", blob })}
        onPhotoRemoved={() => setPhotoPending({ kind: "remove" })}
      />

      {error !== null && (
        <div
          role="alert"
          className="rounded-md border border-coral-deep/30 bg-coral-soft/40 px-4 py-3 font-sans text-[13px] text-coral-deep"
        >
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center px-6 py-3 rounded-md font-sans font-semibold text-white shadow-md disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
          style={{
            background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
            boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
          }}
        >
          {isSaving ? "Saving…" : isEdit ? "Save changes" : "Add child"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="px-5 py-2 rounded-md font-sans text-[14px] font-medium text-ink bg-surface border border-edge hover:border-coral-deep hover:text-coral-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

const MONTH_OPTIONS = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
] as const;
